'use client';

import { useEffect, useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';

export default function WordPressCallbackPage() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authorization...');
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (log: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${log}`]);
  };

  useEffect(() => {
    const handleAuth = async () => {
      try {
        addLog('WordPress.com OAuth callback received');
        
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');

        if (error) {
          addLog(`OAuth error: ${error}`);
          throw new Error(`Authorization failed: ${error}`);
        }

        if (!code) {
          addLog('No authorization code received');
          throw new Error('No authorization code received');
        }

        addLog(`Authorization code received: ${code.substring(0, 10)}...`);
        addLog(`State parameter: ${state}`);

        // Try to communicate with the opener window
        if (window.opener && !window.opener.closed) {
          addLog('Attempting to post message to opener window');
          
          // Get the target origin from the state or use a default
          const targetOrigin = window.location.origin;
          addLog(`Attempting to post message to target origin: ${targetOrigin}`);

          window.opener.postMessage({
            type: 'WORDPRESS_AUTH_SUCCESS',
            code: code,
            state: state
          }, targetOrigin);
          
          addLog('Message posted to opener window');
          setStatus('success');
          setMessage('Authorization successful! You can close this window.');
          
          // Auto-close after 2 seconds
          setTimeout(() => {
            addLog('Auto-closing window...');
            window.close();
          }, 2000);
        } else {
          addLog('No opener window found, using localStorage');
        }

        // Always use localStorage as backup/primary method
        const authData = {
          type: 'WORDPRESS_AUTH_SUCCESS',
          code: code,
          state: state,
          sessionId: state, // Use the state parameter as session ID
          timestamp: Date.now()
        };
        
        try {
          localStorage.setItem('wordpress_auth_result', JSON.stringify(authData));
          addLog('Stored auth result in localStorage');
        } catch (e) {
          addLog(`Failed to store in localStorage: ${e}`);
        }

        setStatus('success');
        setMessage('Authorization successful! Returning to the app...');

      } catch (error: any) {
        addLog(`Error: ${error.message}`);
        setStatus('error');
        setMessage(error.message || 'Authorization failed');
      }
    };

    handleAuth();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        {status === 'loading' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{message}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Success!</h2>
            <p className="text-gray-600">{message}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Authorization Failed</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <button
              onClick={() => window.close()}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Close Window
            </button>
          </div>
        )}

        {/* Debug logs */}
        {logs.length > 0 && (
          <div className="mt-6 p-4 bg-gray-100 rounded text-xs font-mono max-h-48 overflow-y-auto">
            {logs.map((log, i) => (
              <div key={i} className="text-gray-700">{log}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
